<template>
  <div class="chart-container">
    <p class="d-none">
      {{ this.chartData.series }}{{ this.chartOptions.xAxis }}
      {{ legend }}
    </p>
    <bar-chart :options="chartOptions" ref="barChart"></bar-chart>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'SensorBarChart',
  components: {
    barChart: Chart
  },
  props: {
    chartData: {
      type: Object,
      required: true
    },
    boxshadow: {
      type: Boolean,
      required: false,
      default: false
    },
    colors: {
      type: Array,
      default: () => ['#5B9BD5', '#ED7D31']
    },
    height: {
      type: Number,
      default: 300
    },
    legend: {
      type: Object,
      default: () => ({
        align: 'right',
        verticalAlign: 'top',
        y: -10,
        symbolPadding: 10,
        symbolWidth: 6,
        symbolHeight: 30,
        itemStyle: {
          color: '#8394b4',
          fontSize: '9px',
          fontWeight: '500'
        }
      })
    },
    title: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      chartOptions: {
        colors: this.colors,
        chart: {
          height: this.height,
          type: 'columnrange'
        },
        title: this.title,
        yAxis: this.chartData.yAxis,
        xAxis: this.chartData.xAxis,
        series: this.chartData.series,
        credits: {
          enabled: false
        },
        plotOptions: {
          columnrange: {
            borderRadius: 0,
            borderWidth: 0,
            grouping: false,
            dataLabels: {
              enabled: false
            }
          },
          spline: {
            marker: {
              enabled: true,
              radius: 4,
              fillColor: 'white',
              lineWidth: 2,
              lineColor: null
            },
            dataLabels: {
              enabled: false
            },
            enableMouseTracking: true
          }
        },
        legend: this.legend,
        tooltip: {
          useHTML: true,
          borderColor: '#FFFFFF',
          backgroundColor: '#fff',
          color: '#626262',
          borderWidth: 0,
          shadow: false,
          style: {
            color: '#626262'
          },
          formatter: function () {
            const x = this.x
            const series = this.series
            const point = this.point

            const seriesName = String(series.name || '').toLowerCase()
            const isTemperature = seriesName.includes('temp')
            const isHumidity = seriesName.includes('humid')
            const unit = isTemperature
              ? (JSON.parse(localStorage.getItem('tempIndicator')) ? '°C' : '°F')
              : isHumidity
                ? '%'
                : ''

            let value

            // Handle different series types safely
            if (series.type === 'columnrange') {
              const low = point.low
              const high = point.high
              const avgvalue = point.options.y
              return `
                <b>${x}</b><br/>
                <b>Min :</b> ${low.toFixed(1)} ${unit}<br/>
                <b>Max :</b> ${high.toFixed(1)} ${unit}<br/>
                <b>Avg :</b> ${avgvalue.toFixed(1)} ${unit}
              `
            }

            if (series.type === 'spline') {
              value = point.y
              const low = point.options.low
              const high = point.options.high
              return `
                <b>${x}</b><br/>
                <b>Min :</b> ${low.toFixed(1)} ${unit}<br/>
                <b>Max :</b> ${high.toFixed(1)} ${unit}<br/>
                <b>Avg :</b> ${value.toFixed(1)} ${unit}
              `
            }

            // Fallback (safety)
            value = point.y !== undefined ? point.y : ''

            return `
              <b>${x}</b><br/>
              ${value} ${unit}
            `
          }
        }
      }
    }
  },
  methods: {
    fetchNewData (series) {
      this.chartOptions.series = series
    }
  },
  beforeUpdate () {
    this.fetchNewData(this.chartData.series)
    this.chartOptions.xAxis = this.chartData.xAxis
  },
  created () {
    this.fetchNewData(this.chartData.series)
    this.chartOptions.xAxis = this.chartData.xAxis
  }
}
</script>

<style scoped>
.chart-container {
  max-height: 588px;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.highcharts-root {
  padding: 0px;
}

.make-bold {
  font-weight: 600;
}
</style>
